import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useQuery } from '@tanstack/react-query';
import { 
  TrendingDown, 
  TrendingUp, 
  
  Filter,
  RefreshCw,
  BarChart3,
  Target,
  Zap
} from 'lucide-react';
import { fetchLiquidations, LiquidationData } from '../lib/coinglass';

interface LiquidationHeatmapProps {
  className?: string;
}

interface ProcessedLiquidationData extends LiquidationData {
  liquidationRatio: number;
  intensity: number;
  trend: 'up' | 'down' | 'neutral';
}

const LiquidationsHeatmap: React.FC<LiquidationHeatmapProps> = ({ className = '' }) => {
  const [selectedTimeframe, setSelectedTimeframe] = useState('24h');
  const [sortBy, setSortBy] = useState<'total' | 'long' | 'short' | 'ratio'>('total');
  const [filterSymbol, setFilterSymbol] = useState('');

  const { data: liquidationData, error, isLoading, refetch } = useQuery<LiquidationData[]>({
    queryKey: ['liquidationsHeatmap', selectedTimeframe],
    queryFn: () => fetchLiquidations(selectedTimeframe),
    refetchInterval: 60000, // 1 minute
  });

  const processedData: ProcessedLiquidationData[] = React.useMemo(() => {
    if (!liquidationData) return [];

    return liquidationData
      .filter(item => 
        filterSymbol ? item.symbol.toLowerCase().includes(filterSymbol.toLowerCase()) : true
      )      .map(item => {
        const total = item.liquidation_usd_24h;
        const longRatio = total > 0 ? (item.long_liquidation_usd_24h / total) * 100 : 50;
        
        return {
          ...item,
          liquidationRatio: longRatio,
          intensity: Math.min(total / 10000000, 1), // Normalize to 0-1 based on $10M max
          trend: (longRatio > 60 ? 'up' : longRatio < 40 ? 'down' : 'neutral') as 'up' | 'down' | 'neutral'
        };
      })
      .sort((a, b) => {
        switch (sortBy) {
          case 'total': return b.liquidation_usd_24h - a.liquidation_usd_24h;
          case 'long': return b.long_liquidation_usd_24h - a.long_liquidation_usd_24h;
          case 'short': return b.short_liquidation_usd_24h - a.short_liquidation_usd_24h;
          case 'ratio': return Math.abs(b.liquidationRatio - 50) - Math.abs(a.liquidationRatio - 50);
          default: return 0;
        }
      })
      .slice(0, 20); // Top 20 for better visualization
  }, [liquidationData, sortBy, filterSymbol]);

  const formatUSD = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      notation: 'compact',
      maximumFractionDigits: 2
    }).format(value);
  };

  const getIntensityColor = (intensity: number, type: 'long' | 'short') => {
    const opacity = Math.max(intensity, 0.1);
    if (type === 'long') {
      return `rgba(34, 197, 94, ${opacity})`; // Green for longs
    } else {
      return `rgba(239, 68, 68, ${opacity})`; // Red for shorts
    }
  };

  const HeatmapCell: React.FC<{ item: ProcessedLiquidationData; index: number }> = ({ item, index }) => {
    const [isHovered, setIsHovered] = useState(false);

    return (
      <motion.div
        className="relative group"
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: index * 0.02 }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4 hover:border-zinc-600 transition-all duration-200 min-h-[120px]">
          {/* Symbol Header */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-gradient-to-br from-orange-500 to-red-500 rounded-full flex items-center justify-center text-white font-bold text-sm">
                {item.symbol.substring(0, 2)}
              </div>
              <div>
                <div className="font-bold text-white text-sm">{item.symbol}</div>
                <div className="text-xs text-gray-400">
                  {item.trend === 'up' ? (
                    <TrendingUp className="w-3 h-3 inline text-green-500" />
                  ) : item.trend === 'down' ? (
                    <TrendingDown className="w-3 h-3 inline text-red-500" />
                  ) : (
                    <Target className="w-3 h-3 inline text-gray-500" />
                  )}
                </div>
              </div>
            </div>
            <div className="text-xs text-gray-400">
              {(item.liquidationRatio).toFixed(1)}% L
            </div>
          </div>

          {/* Liquidation Bar */}
          <div className="mb-3">
            <div className="flex justify-between text-xs text-gray-400 mb-1">
              <span>Long</span>
              <span>Short</span>
            </div>
            <div className="h-3 bg-zinc-800 rounded-full overflow-hidden flex">
              <motion.div
                className="h-full bg-green-500"
                style={{ 
                  width: `${item.liquidationRatio}%`,
                  backgroundColor: getIntensityColor(item.intensity, 'long')
                }}
                initial={{ width: 0 }}
                animate={{ width: `${item.liquidationRatio}%` }}
                transition={{ duration: 1, delay: index * 0.05 }}
              />
              <motion.div
                className="h-full bg-red-500"
                style={{ 
                  width: `${100 - item.liquidationRatio}%`,
                  backgroundColor: getIntensityColor(item.intensity, 'short')
                }}
                initial={{ width: 0 }}
                animate={{ width: `${100 - item.liquidationRatio}%` }}
                transition={{ duration: 1, delay: index * 0.05 }}
              />
            </div>
          </div>

          {/* Total Liquidation */}
          <div className="text-center">
            <div className="text-lg font-bold text-white">
              {formatUSD(item.liquidation_usd_24h)}
            </div>
            <div className="text-xs text-gray-400">Total Liquidated</div>
          </div>

          {/* Hover Details */}
          <AnimatePresence>
            {isHovered && (
              <motion.div
                className="absolute inset-0 bg-zinc-800 border border-zinc-600 rounded-lg p-3 z-10"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <div className="text-sm space-y-2">
                  <div className="font-bold text-white border-b border-zinc-600 pb-1">
                    {item.symbol} Liquidations
                  </div>
                  <div className="flex justify-between">
                    <span className="text-green-400">Long:</span>
                    <span className="text-white">{formatUSD(item.long_liquidation_usd_24h)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-red-400">Short:</span>
                    <span className="text-white">{formatUSD(item.short_liquidation_usd_24h)}</span>
                  </div>
                  <div className="flex justify-between border-t border-zinc-600 pt-1">
                    <span className="text-gray-400">Ratio:</span>
                    <span className="text-white">{item.liquidationRatio.toFixed(1)}% / {(100 - item.liquidationRatio).toFixed(1)}%</span>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    );
  };

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-xl font-bold text-white mb-1">Liquidations Heatmap</h3>
          <p className="text-sm text-gray-400">Real-time liquidation activity across markets</p>
        </div>
        <button
          onClick={() => refetch()}
          className="p-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
        >
          <RefreshCw className="w-4 h-4 text-white" />
        </button>
      </div>

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex items-center space-x-2">
          <Filter className="w-4 h-4 text-gray-400" />
          <select
            value={selectedTimeframe}
            onChange={(e) => setSelectedTimeframe(e.target.value)}
            className="bg-zinc-800 border border-zinc-700 rounded px-3 py-1 text-white text-sm"
          >
            <option value="24h">24 Hours</option>
            <option value="12h">12 Hours</option>
            <option value="4h">4 Hours</option>
          </select>
        </div>

        <div className="flex items-center space-x-2">
          <BarChart3 className="w-4 h-4 text-gray-400" />
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as 'total' | 'long' | 'short' | 'ratio')}
            className="bg-zinc-800 border border-zinc-700 rounded px-3 py-1 text-white text-sm"
          >
            <option value="total">Sort by Total</option>
            <option value="long">Sort by Longs</option>
            <option value="short">Sort by Shorts</option>
            <option value="ratio">Sort by Ratio</option>
          </select>
        </div>

        <input
          type="text"
          placeholder="Filter symbols..."
          value={filterSymbol}
          onChange={(e) => setFilterSymbol(e.target.value)}
          className="bg-zinc-800 border border-zinc-700 rounded px-3 py-1 text-white text-sm placeholder-gray-400"
        />
      </div>

      {/* Legend */}
      <div className="flex items-center justify-center space-x-6 text-sm">
        <div className="flex items-center">
          <div className="w-3 h-3 bg-green-500 rounded mr-2" />
          <span className="text-gray-400">Long Liquidations</span>
        </div>
        <div className="flex items-center">
          <div className="w-3 h-3 bg-red-500 rounded mr-2" />
          <span className="text-gray-400">Short Liquidations</span>
        </div>
        <div className="flex items-center">
          <Zap className="w-3 h-3 text-yellow-500 mr-2" />
          <span className="text-gray-400">Intensity = Volume</span>
        </div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {Array.from({ length: 12 }).map((_, i) => (
            <div key={i} className="bg-zinc-900 border border-zinc-800 rounded-lg p-4 min-h-[120px]">
              <div className="animate-pulse space-y-3">
                <div className="flex items-center space-x-2">
                  <div className="w-8 h-8 bg-zinc-700 rounded-full" />
                  <div className="h-4 bg-zinc-700 rounded w-12" />
                </div>
                <div className="h-3 bg-zinc-700 rounded w-full" />
                <div className="h-6 bg-zinc-700 rounded w-20 mx-auto" />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Data unavailable — component hides gracefully */}
      {error && null}

      {/* Heatmap Grid */}
      {processedData.length > 0 && (
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          {processedData.map((item, index) => (
            <HeatmapCell key={item.symbol} item={item} index={index} />
          ))}
        </motion.div>
      )}

      {/* Summary Stats */}
      {processedData.length > 0 && (
        <motion.div
          className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-4"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
            <div>
              <div className="text-2xl font-bold text-green-400">
                {formatUSD(processedData.reduce((sum, item) => sum + item.long_liquidation_usd_24h, 0))}
              </div>
              <div className="text-sm text-gray-400">Total Long Liquidations</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-red-400">
                {formatUSD(processedData.reduce((sum, item) => sum + item.short_liquidation_usd_24h, 0))}
              </div>
              <div className="text-sm text-gray-400">Total Short Liquidations</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-white">
                {processedData.length}
              </div>
              <div className="text-sm text-gray-400">Active Markets</div>
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default React.memo(LiquidationsHeatmap);
